import React, { useEffect, useState, useContext } from "react";
import { Button, Row, Col, Radio, Tag } from "antd";
import { SaveOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  Paper,
  TextField,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { URL as BASE_URL } from "../config";
import { LoaderContext } from "../Components/dashboard";

const themeColor = "#ff002b";

export default function AddPdfCategoryForm() {
  const { setLoading } = useContext(LoaderContext);
  const { id } = useParams();
  const navigate = useNavigate();

  const [categoryName, setCategoryName] = useState("");
  const [isActive, setIsActive] = useState(1);
  const [isDefault, setIsDefault] = useState(0);
  const [isOn, setIsOn] = useState(1);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState(id ? "edit" : "create");

  useEffect(() => {
    if (id) fetchDetails();
  }, [id]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/pdfcategory/${id}`);
      const data = res.data.data;

      setCategoryName(data.pdfCategoryName || "");
      setIsActive(data.isActive ?? 1);
      setIsDefault(data.isDefault ?? 0);
      setIsOn(data.isOn ?? 1);

      setLoading(false);
    } catch (err) {
      toast.error("Failed to load details");
      setLoading(false);
    }
  };

  const clearForm = () => {
    setCategoryName("");
    setIsActive(1);
    setIsDefault(0);
    setIsOn(1);
  };

  const handleSave = async () => {
    if (saving) return;

    if (!categoryName.trim()) {
      toast.error("Category name is required");
      return;
    }

    try {
      setSaving(true);

      const formData = new FormData();
      formData.append("pdfCategoryName", categoryName);
      formData.append("isActive", isActive);
      formData.append("isDefault", isDefault);
      formData.append("isOn", isOn);

      if (mode === "edit") {
        await axios.patch(`${BASE_URL}/pdfcategory/${id}`, formData);
        toast.success("Category updated successfully");
      } else {
        await axios.post(`${BASE_URL}/pdfcategory`, formData);
        toast.success("Category created successfully");
      }

      clearForm();
      navigate("/dashboard/pdfcategorylist");

    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
        
        <Box display="flex" justifyContent="center" mb={2}>
          <Box
             sx={{
              background: `linear-gradient(90deg, ${themeColor}, #ff6a6a)`,
              px: { xs: 2.5, md: 4 },
              py: { xs: 0.8, md: 1.1 },
              borderRadius: "8px 22px 8px 22px",
              boxShadow: "0 6px 16px rgba(255,92,110,0.35)",
            }}
          >
            <Typography
               sx={{
                fontSize: "18px",
                fontWeight: 900,
                color: "#fff",
                letterSpacing: "0.7px",
                textTransform: "uppercase",
              }}
            >
              Add PDF Category
            </Typography>
          </Box>
        </Box>

        <Divider    sx={{
            mb: 2.2,
            background: `linear-gradient(90deg, transparent, ${themeColor}, transparent)`,
            height: "2px",
            borderRadius: 5,
          }} />

        <Row gutter={[16, 16]}>

          {/* Category Name */}
          <Col xs={24} md={8}>
            <Typography fontWeight={600} mb={0.5} sx={{ color: themeColor, fontSize: { xs: "13px", md: "14px" } }}>
              Category Name
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="Enter category name"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
  sx={{
                "& .MuiOutlinedInput-root": {
                  minHeight: 40,
                  "& fieldset": { borderColor: "#ccc" },
                  "&:hover fieldset": { borderColor: "#aaa" },
                  "&.Mui-focused fieldset": { borderColor: "#aaa" },
                },
                "& .MuiInputBase-input": {
                  fontSize: "13px",
                  padding: "8.5px 12px",
                },
              }}
            />
          </Col>

          {/* Active Status */}
          <Col xs={24} md={8}>
            <Typography fontWeight={600} mb={1} sx={{ color: themeColor, fontSize: { xs: "13px", md: "14px" } }}>
              Status
            </Typography>
            <Radio.Group
              value={isActive}
              onChange={(e) => setIsActive(e.target.value)}
            >
              <Radio value={1}>
                <Tag color="green">Active</Tag>
              </Radio>
              <Radio value={0}>
                <Tag color="red">Deactive</Tag>
              </Radio>
            </Radio.Group>
          </Col>

        </Row>

        {/* Buttons */}
        <Box display="flex" justifyContent="end" mt={4} gap={2}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            style={{
              background: themeColor,
              borderRadius: 25,
              padding: "4px 20px",
            }}
          >
            {mode === "edit" ? "Update" : "Save"}
          </Button>

          <Button
            icon={<InfoCircleOutlined />}
            onClick={() => navigate("/dashboard/pdfcategorylist")}
            style={{
              borderRadius: 25,
              padding: "4px 20px",
              color: "red",
              borderColor: "red",
            }}
          >
            Details
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
